import CircleIcon from "@mui/icons-material/Circle";
import { Autocomplete, Box, Grid, TextField } from "@mui/material";
import type React from "react";
import { type Dispatch, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { labToCss } from "service/color-math";
import {
  convertNamedColorToRalDesignPlusColor,
  loadRalDesignPlusColors,
  type RalDesignPlusColor,
} from "service/ral-design-plus-colors";
import type { NamedColor } from "./ColorFilter";

export interface ColorFilterRalDesignPlusProps {
  onChange: Dispatch<NamedColor | undefined>;
  value?: NamedColor;
}

export const ColorFilterRalDesignPlus = (
  props: ColorFilterRalDesignPlusProps,
) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<Array<RalDesignPlusColor>>([]);
  const isStartLoading = open && options.length === 0;
  const currentLanguage = i18n.language;

  useEffect(() => {
    if (options.length > 0) {
      loadRalDesignPlusColors().then(setOptions);
    }
  }, [currentLanguage, options.length]);

  useEffect(() => {
    if (isStartLoading) {
      loadRalDesignPlusColors().then(setOptions);
    }
  }, [isStartLoading]);

  const onChange = (
    _event: React.SyntheticEvent,
    option: RalDesignPlusColor | null,
  ) => {
    const value: NamedColor | undefined = option
      ? {
          value: option.lab,
          name: option.name,
          code: option.code,
        }
      : undefined;
    props.onChange(value);
  };

  return (
    <Grid size={{ xs: 12, md: 6, xl: 3 }}>
      <Autocomplete
        value={
          props.value
            ? convertNamedColorToRalDesignPlusColor(props.value)
            : null
        }
        isOptionEqualToValue={(o, v) => o.name === v.name}
        onChange={onChange}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        loading={isStartLoading}
        options={options}
        fullWidth
        getOptionLabel={(option) => option.name}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t("filter_color_code_ral_design_plus")}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <CircleIcon
              sx={{ color: labToCss(option.lab), marginRight: "10px" }}
            />
            {option.name}
          </Box>
        )}
      />
    </Grid>
  );
};
